/* eslint-disable @typescript-eslint/no-explicit-any */
import { scheduleSync } from './sync-service';

const BASE_URL = '/api';

// Set by AuthBridge once Clerk is ready. Called per-request to get a fresh JWT.
// Stays null in Electron/local mode (no auth needed).
let _tokenProvider: (() => Promise<string | null>) | null = null;
export function setTokenProvider(fn: (() => Promise<string | null>) | null) {
  _tokenProvider = fn;
}

// LAN-control / desktop-session token sent as X-Desktop-Token. Set in LAN control
// mode (host-issued token via QR) so the LAN device's REST calls authenticate
// without a Clerk sign-in.
let _desktopToken: string | null = null;
export function setDesktopToken(token: string | null) {
  _desktopToken = token;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = _tokenProvider ? await _tokenProvider() : null;
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(_desktopToken ? { 'X-Desktop-Token': _desktopToken } : {}),
      ...(options?.headers as Record<string, string> | undefined),
    },
  });
  const json = await res.json();
  if (!json.success) {
    const err = new Error(json.error || 'Error desconocido') as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  const method = (options?.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') scheduleSync();
  return json.data;
}

// FormData requests (image/font upload) also need the auth token
async function requestForm<T>(path: string, method: string, body: FormData): Promise<T> {
  const token = _tokenProvider ? await _tokenProvider() : null;
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    body,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(_desktopToken ? { 'X-Desktop-Token': _desktopToken } : {}),
    },
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Error desconocido');
  scheduleSync();
  return json.data;
}

export const api = {
  // Presentations
  getPresentations: () => request<any[]>('/presentations'),
  getPresentation: (id: string) => request<any>(`/presentations/${id}`),
  createPresentation: (data: { name: string; category?: string }) =>
    request<any>('/presentations', { method: 'POST', body: JSON.stringify(data) }),
  updatePresentation: (id: string, data: { name?: string; category?: string }) =>
    request<any>(`/presentations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePresentation: (id: string) =>
    request<boolean>(`/presentations/${id}`, { method: 'DELETE' }),

  // Slides
  getSlides: (presentationId: string) =>
    request<any[]>(`/slides/presentation/${presentationId}`),
  createSlide: (data: any) =>
    request<any>('/slides', { method: 'POST', body: JSON.stringify(data) }),
  updateSlide: (id: string, data: any) =>
    request<any>(`/slides/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSlide: (id: string) => request<boolean>(`/slides/${id}`, { method: 'DELETE' }),
  duplicateSlide: (id: string) =>
    request<any>(`/slides/${id}/duplicate`, { method: 'POST' }),
  copySlides: (data: { targetPresentationId: string; slideIds: string[]; groupLabel?: string }) =>
    request<any[]>('/slides/copy', { method: 'POST', body: JSON.stringify(data) }),
  reorderSlides: (presentationId: string, slideIds: string[]) =>
    request<boolean>(`/slides/presentation/${presentationId}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ slideIds }),
    }),
  // Slide Groups
  getSlideGroups: (presentationId: string) =>
    request<any[]>(`/slide-groups/presentation/${presentationId}`),
  createSlideGroup: (data: { presentationId: string; label: string; orderNum?: number }) =>
    request<any>('/slide-groups', { method: 'POST', body: JSON.stringify(data) }),
  updateSlideGroup: (id: string, data: { label?: string; orderNum?: number }) =>
    request<any>(`/slide-groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSlideGroup: (id: string) =>
    request<boolean>(`/slide-groups/${id}`, { method: 'DELETE' }),
  reorderSlideGroups: (presentationId: string, groupIds: string[]) =>
    request<boolean>(`/slide-groups/presentation/${presentationId}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ groupIds }),
    }),

  // Slide Sections
  getSlideSections: (groupId: string) =>
    request<any[]>(`/slide-sections/group/${groupId}`),
  createSlideSection: (data: { groupId: string; label: string; orderNum?: number }) =>
    request<any>('/slide-sections', { method: 'POST', body: JSON.stringify(data) }),
  updateSlideSection: (id: string, data: { label?: string; orderNum?: number }) =>
    request<any>(`/slide-sections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSlideSection: (id: string) =>
    request<boolean>(`/slide-sections/${id}`, { method: 'DELETE' }),
  reorderSlideSections: (groupId: string, sectionIds: string[]) =>
    request<boolean>(`/slide-sections/group/${groupId}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ sectionIds }),
    }),
  applyStyle: (slideIds: string[], style: any, sourceElements?: any[]) =>
    request<boolean>('/slides/apply-style', {
      method: 'POST',
      body: JSON.stringify({ slideIds, style, ...(sourceElements ? { sourceElements } : {}) }),
    }),

  // Bible
  getBibleVersions: () => request<string[]>('/bible/versions'),
  getBibleBooks: () => request<any[]>('/bible/books'),
  getBibleChapters: (bookId: number, version: string) =>
    request<number[]>(`/bible/books/${bookId}/chapters?version=${version}`),
  getBibleVerses: (bookId: number, chapter: number, version: string) =>
    request<any[]>(`/bible/${bookId}/${chapter}?version=${version}`),
  searchBible: (q: string, version: string) =>
    request<any[]>(`/bible/search?q=${encodeURIComponent(q)}&version=${version}`),

  // Songs
  getSongs: () => request<any[]>('/songs'),
  getSong: (id: string) => request<any>(`/songs/${id}`),
  createSong: (data: any) =>
    request<any>('/songs', { method: 'POST', body: JSON.stringify(data) }),
  updateSong: (id: string, data: any) =>
    request<any>(`/songs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSong: (id: string) => request<boolean>(`/songs/${id}`, { method: 'DELETE' }),
  searchSongs: (q: string) => request<any[]>(`/songs/search?q=${encodeURIComponent(q)}`),
  addSongSection: (songId: string, data: any) =>
    request<any>(`/songs/${songId}/sections`, { method: 'POST', body: JSON.stringify(data) }),
  updateSongSection: (sectionId: string, data: { type?: string; label?: string; lyrics?: string }) =>
    request<any>(`/songs/sections/${sectionId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSongSection: (sectionId: string) =>
    request<boolean>(`/songs/sections/${sectionId}`, { method: 'DELETE' }),
  createArrangement: (songId: string, name: string, sectionOrder: string[]) =>
    request<any>(`/songs/${songId}/arrangements`, { method: 'POST', body: JSON.stringify({ name, sectionOrder }) }),
  updateArrangement: (songId: string, arrangementId: string, data: { name?: string; sectionOrder?: string[] }) =>
    request<any>(`/songs/${songId}/arrangements/${arrangementId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteArrangement: (songId: string, arrangementId: string) =>
    request<boolean>(`/songs/${songId}/arrangements/${arrangementId}`, { method: 'DELETE' }),

  // Images
  getImages: () => request<{ id: string; filename: string; url: string; cloudUrl: string | null; originalName: string | null; size: number | null; createdAt: number }[]>('/images'),

  uploadImage: (file: File): Promise<{ id: string; filename: string; url: string; cloudUrl: string | null; originalName: string | null; isDuplicate: boolean }> => {
    const fd = new FormData();
    fd.append('file', file);
    return requestForm('/images', 'POST', fd);
  },

  deleteImage: async (id: string): Promise<boolean> => {
    const result = await request<boolean>(`/images/${encodeURIComponent(id)}`, { method: 'DELETE' });
    window.electronAPI?.images.clearCache();
    return result;
  },

  // Fonts
  getFonts: () => request<any[]>('/fonts'),

  detectFontFile: (file: File): Promise<{ weight: number; style: 'normal' | 'italic'; variable: boolean } | null> => {
    const fd = new FormData();
    fd.append('file', file);
    return requestForm<{ weight: number; style: 'normal' | 'italic'; variable: boolean }>('/fonts/detect', 'POST', fd).catch(() => null);
  },

  createFontFamily: (label: string) =>
    request<any>('/fonts', { method: 'POST', body: JSON.stringify({ label }) }),

  addFontFile: (fontId: string, file: File, weight: number, style: string, variable: boolean) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('weight', String(weight));
    fd.append('style', style);
    fd.append('variable', String(variable));
    return requestForm<any>(`/fonts/${fontId}/files`, 'POST', fd);
  },

  deleteFontFile: (fileId: string) =>
    request<boolean>(`/fonts/files/${fileId}`, { method: 'DELETE' }),

  deleteFont: (id: string) => request<boolean>(`/fonts/${id}`, { method: 'DELETE' }),

  // Network info: LAN IPs + port for connecting other devices as WS receivers.
  getCastToken: () => request<string>('/cast-token'),
  getNetwork: () => request<{ ips: string[]; interfaces: { name: string; label: string; ip: string }[]; port: number; hostname: string }>('/network'),

  // LAN control: host mints a token to embed in the control QR (12 h, fixed TTL).
  getLanToken: () => request<string>('/auth/lan-token', { method: 'POST' }),

  // Sync org name/slug from Clerk into local DB (fixes fallback INSERT that only has slug).
  patchOrg: (name: string, slug: string) =>
    request<void>('/org', { method: 'PATCH', body: JSON.stringify({ name, slug }) }),
};
